import React from 'react'
import Link from 'next/link'
import { css } from '@emotion/react'
import { colors, mq } from 'plume-ui'
import { FullColorLogo, LOGO_SIZE } from '../../common/Logo'
import { IcomoonIcon } from '../../atoms/IcomoonIcon'
import {
  enterpriseUrl,
  trialRequestUrl,
  downloadDocumentUrl,
  customerInterviewsUrl,
  seminarsUrl,
  resourcesUrl,
} from '@/lib/url'
import { EnterpriseButton } from './EnterpriseButton'
import { MobileSideMenu } from '../../view/MobileSideMenu'

export type EnterpriseTabType =
  | 'features'
  | 'customers'
  | 'events'
  | 'resources'

type Props = {
  activeTab?: EnterpriseTabType
  background?: boolean
}

type DeviceProps = {
  activeTab?: EnterpriseTabType
  background?: boolean
  scrollPosition: boolean
}

export const Header: React.FC<Props> = (props) => {
  const requestId = React.useRef(0)
  const [scrollPosition, setScrollPosition] = React.useState(false)

  const checkScrollPosition = () => {
    requestId.current = requestAnimationFrame(checkScrollPosition)
    setScrollPosition(0 < window.scrollY)
  }

  React.useEffect(() => {
    checkScrollPosition()
    return () => cancelAnimationFrame(requestId.current)
  }, [])

  return (
    <header
      css={css`
        position: fixed;
        top: 0;
        z-index: 40;
        width: 100%;
      `}
    >
      <DesktopHeader {...props} scrollPosition={scrollPosition} />
      <MobileHeader {...props} scrollPosition={scrollPosition} />
    </header>
  )
}

export const ENTERPRISE_DESKTOP_HEADER_HEIGHT = '72px'

const DesktopHeader: React.FC<DeviceProps> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        background-color: ${props.background || props.scrollPosition
          ? colors.white00
          : 'transparent'};
        border-bottom: 1px solid
          ${props.background || props.scrollPosition
            ? colors.borderLight
            : 'transparent'};
        width: 100%;
        height: ${ENTERPRISE_DESKTOP_HEADER_HEIGHT};
        transition: background-color 0.3s;
        ${mq.untilMedium} {
          display: none;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          max-width: 1440px;
          margin-inline: auto;
          padding-inline: 40px;
        `}
      >
        <nav
          css={css`
            display: flex;
          `}
        >
          <p
            css={css`
              margin: 0;
              display: flex;
              align-items: center;
            `}
          >
            <Link href={enterpriseUrl} passHref>
              <a target="_blank">
                <FullColorLogo size={LOGO_SIZE.SMALL} />
              </a>
            </Link>
          </p>
          <ul
            css={css`
              margin: 0;
              display: flex;
              grid-column-gap: 32px;
            `}
          >
            <HeaderLink
              href={enterpriseUrl}
              active={props.activeTab === 'features'}
            >
              Speedaとは
            </HeaderLink>
            <HeaderLink
              href={customerInterviewsUrl}
              active={props.activeTab === 'customers'}
            >
              導入事例
            </HeaderLink>
            <HeaderLink
              href={seminarsUrl}
              active={props.activeTab === 'events'}
            >
              セミナー・イベント
            </HeaderLink>
            <HeaderLink
              href={resourcesUrl}
              active={props.activeTab === 'resources'}
            >
              資料・レポート
            </HeaderLink>
          </ul>
        </nav>
        <p
          css={css`
            margin: 0;
            display: flex;
            grid-column-gap: 8px;
          `}
        >
          <EnterpriseButton
            href={trialRequestUrl}
            external={true}
            type="outline"
            data-testid="trial-page-button"
          >
            無料トライアル
          </EnterpriseButton>
          <EnterpriseButton href={downloadDocumentUrl} external={true}>
            資料ダウンロード
          </EnterpriseButton>
        </p>
      </div>
    </div>
  )
}

export const ENTERPRISE_MOBILE_HEADER_HEIGHT = '56px'

const MobileHeader: React.FC<DeviceProps> = (props) => {
  const [sideMenuOpened, setSideMenuOpened] = React.useState(false)
  const toggleIsOpen = () => {
    setSideMenuOpened(!sideMenuOpened)
  }

  return (
    <div
      css={css`
        position: relative;
        background-color: ${props.background || props.scrollPosition
          ? colors.white00
          : 'transparent'};
        width: 100%;
        height: ${ENTERPRISE_MOBILE_HEADER_HEIGHT};
        padding-block: 16px;
        padding-inline: 24px;
        border-bottom: 1px solid
          ${props.background || props.scrollPosition
            ? colors.border
            : 'transparent'};
        ${mq.large} {
          display: none;
        }
      `}
    >
      <p
        css={css`
          margin: 0;
        `}
      >
        <Link href={enterpriseUrl} passHref>
          <a target="_blank">
            <FullColorLogo size={LOGO_SIZE.SMALL} />
          </a>
        </Link>
      </p>
      <button
        onClick={toggleIsOpen}
        css={css`
          background: 0;
          border: 0;
          position: absolute;
          right: 0;
          top: 0;
          padding-block: 16px;
          padding-inline: 16px;
          cursor: pointer;
        `}
      >
        <IcomoonIcon
          name="menu"
          label="メニューを開く"
          css={css`
            font-size: 24px;
          `}
        ></IcomoonIcon>
      </button>
      <MobileSideMenu
        isOpen={sideMenuOpened}
        toggleIsOpen={toggleIsOpen}
        pageType="enterprise"
      />
    </div>
  )
}

type HeaderLinkProps = {
  href: string
  active?: boolean
  children: React.ReactNode
}

const HeaderLink: React.FC<HeaderLinkProps> = (props) => {
  return (
    <li>
      <Link href={props.href} passHref>
        <a
          target="_blank"
          css={css`
            display: flex;
            align-items: center;
            padding-block: 15px;
            font-size: 14px;
            line-height: 1.25;
            letter-spacing: 0.02em;
            color: ${colors.text};
            border-bottom: 2px solid
              ${props.active ? colors.borderStrong : 'transparent'};
            &:hover {
              border-bottom-color: ${colors.borderStrong};
            }
          `}
        >
          {props.children}
        </a>
      </Link>
    </li>
  )
}
